import React from 'react';
import { Filter, X, Calendar, Shield, Activity, Search, ToggleLeft, ToggleRight } from 'lucide-react';

const FilterControls = ({
  filters,
  onFilterChange,
  onClearFilters,
  activityTypes,
  autoRefresh,
  onAutoRefreshChange
}) => {
  const hasActiveFilters = Object.values(filters)?.some(value => value);

  const roleOptions = [
    { value: '', label: 'All Roles' },
    { value: 'admin', label: 'Admin' },
    { value: 'editor', label: 'Editor' },
    { value: 'artist', label: 'Artist' }
  ];

  // Generate preset date options
  const getPresetDateRange = (preset) => {
    const now = new Date();
    let startDate;

    switch (preset) {
      case 'today':
        startDate = new Date(now.setHours(0, 0, 0, 0));
        break;
      case 'yesterday':
        startDate = new Date(now.setDate(now.getDate() - 1));
        startDate?.setHours(0, 0, 0, 0);
        break;
      case 'last7days':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case 'last30days':
        startDate = new Date(now.setDate(now.getDate() - 30));
        break;
      case 'thisMonth':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      default:
        return { startDate: '', endDate: '' };
    }

    return {
      startDate: startDate?.toISOString()?.split('T')?.[0],
      endDate: new Date()?.toISOString()?.split('T')?.[0]
    };
  };

  const handlePresetDateChange = (preset) => {
    const { startDate, endDate } = getPresetDateRange(preset);
    onFilterChange('startDate', startDate);
    onFilterChange('endDate', endDate);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-gray-600" />
          <h3 className="text-lg font-semibold text-gray-900">Filter Activity Logs</h3>
          {hasActiveFilters && (
            <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded-full">
              {Object.values(filters)?.filter(v => v)?.length} active
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-3">
          {/* Auto-refresh toggle */}
          <button
            onClick={() => onAutoRefreshChange(!autoRefresh)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
              autoRefresh 
                ? 'bg-green-50 text-green-700 border border-green-200' :'bg-gray-50 text-gray-600 border border-gray-200'
            }`}
          >
            {autoRefresh ? (
              <ToggleRight className="w-5 h-5" />
            ) : (
              <ToggleLeft className="w-5 h-5" />
            )}
            <span className="text-sm font-medium">
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </span>
          </button>

          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
            >
              <X className="w-4 h-4" />
              Clear All Filters
            </button>
          )}
        </div>
      </div>
      {/* Filter Controls Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Search Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4" />
              Search
            </div>
          </label>
          <input
            type="text"
            value={filters?.search}
            onChange={(e) => onFilterChange('search', e?.target?.value)}
            placeholder="Search users, actions, or descriptions..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Role Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              User Role
            </div>
          </label>
          <select
            value={filters?.role}
            onChange={(e) => onFilterChange('role', e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            {roleOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </div>

        {/* Activity Type Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Action Type
            </div>
          </label>
          <select
            value={filters?.activityType}
            onChange={(e) => onFilterChange('activityType', e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Actions</option>
            {activityTypes?.map(type => (
              <option key={type} value={type}>
                {type?.replace(/_/g, ' ')?.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        {/* Start Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Start Date
            </div>
          </label>
          <input
            type="date"
            value={filters?.startDate}
            onChange={(e) => onFilterChange('startDate', e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* End Date Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              End Date
            </div>
          </label>
          <input
            type="date"
            value={filters?.endDate}
            onChange={(e) => onFilterChange('endDate', e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Date Presets */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Quick Date Range
          </label>
          <select
            onChange={(e) => handlePresetDateChange(e?.target?.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            defaultValue=""
          >
            <option value="">Select preset...</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7days">Last 7 Days</option>
            <option value="last30days">Last 30 Days</option>
            <option value="thisMonth">This Month</option>
          </select>
        </div>
      </div>
      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Active Filters:</span>
            {filters?.role && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                Role: {filters?.role}
                <button onClick={() => onFilterChange('role', '')} className="hover:text-blue-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters?.activityType && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                Type: {filters?.activityType}
                <button onClick={() => onFilterChange('activityType', '')} className="hover:text-purple-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {filters?.search && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">
                Search: {filters?.search}
                <button onClick={() => onFilterChange('search', '')} className="hover:text-green-900">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {(filters?.startDate || filters?.endDate) && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                Date: {filters?.startDate || '...'} to {filters?.endDate || '...'}
                <button 
                  onClick={() => {
                    onFilterChange('startDate', '');
                    onFilterChange('endDate', '');
                  }} 
                  className="hover:text-orange-900"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterControls;